import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';

interface SpeedometerDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress' },
  'principles': { hebrew: 'עקרונות', english: 'Principles' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
};

const SpeedometerDashboard: React.FC<SpeedometerDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
    };
  }, [categoryScores, language]);

  const getSpeedColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const drawSpeedometer = (value: number, size: number = 120) => {
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size * 0.35;
    const startAngle = -135;
    const endAngle = 135;
    const totalAngle = endAngle - startAngle;

    // Calculate needle angle based on value (0-100)
    const needleAngle = startAngle + (totalAngle * value) / 100;
    const needleRad = (needleAngle * Math.PI) / 180;
    const needleEndX = centerX + radius * 0.7 * Math.cos(needleRad);
    const needleEndY = centerY + radius * 0.7 * Math.sin(needleRad);

    // Create arc segments for different score ranges
    const createArc = (startPct: number, endPct: number, color: string) => {
      const start = startAngle + (totalAngle * startPct) / 100;
      const end = startAngle + (totalAngle * endPct) / 100;
      const startRad = (start * Math.PI) / 180;
      const endRad = (end * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);

      const largeArc = (endPct - startPct) > 50 ? 1 : 0;

      return `
        <path d="M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}"
              stroke="${color}" stroke-width="${size * 0.12}" fill="none" stroke-linecap="round"/>
      `;
    };

    // Draw tick marks
    const ticks = [0, 20, 40, 60, 80, 100].map(pct => {
      const angle = startAngle + (totalAngle * pct) / 100;
      const rad = (angle * Math.PI) / 180;
      const x1 = centerX + (radius - size * 0.1) * Math.cos(rad);
      const y1 = centerY + (radius - size * 0.1) * Math.sin(rad);
      const x2 = centerX + (radius + size * 0.05) * Math.cos(rad);
      const y2 = centerY + (radius + size * 0.05) * Math.sin(rad);

      const labelX = centerX + (radius + size * 0.15) * Math.cos(rad);
      const labelY = centerY + (radius + size * 0.15) * Math.sin(rad);

      return `
        <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#666" stroke-width="2"/>
        <text x="${labelX}" y="${labelY}" text-anchor="middle" font-size="${size * 0.1}" fill="#666">${pct}</text>
      `;
    }).join('');

    return `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <!-- Background arc -->
        <circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="none" stroke="#e0e0e0" stroke-width="${size * 0.12}"/>

        <!-- Colored segments -->
        ${createArc(0, 40, '#e74c3c')}
        ${createArc(40, 60, '#f39c12')}
        ${createArc(60, 80, '#2ecc71')}
        ${createArc(80, 100, '#27ae60')}

        <!-- Tick marks -->
        ${ticks}

        <!-- Center circle -->
        <circle cx="${centerX}" cy="${centerY}" r="${size * 0.06}" fill="#333"/>

        <!-- Needle -->
        <line x1="${centerX}" y1="${centerY}" x2="${needleEndX}" y2="${needleEndY}"
              stroke="${getSpeedColor(value)}" stroke-width="${size * 0.025}" stroke-linecap="round"/>

        <!-- Needle center dot -->
        <circle cx="${centerX}" cy="${centerY}" r="${size * 0.04}" fill="${getSpeedColor(value)}" stroke="white" stroke-width="2"/>

        <!-- Value text -->
        <text x="${centerX}" y="${centerY + size * 0.25}" text-anchor="middle" font-size="${size * 0.15}" font-weight="bold" fill="${getSpeedColor(value)}">
          ${value.toFixed(0)}%
        </text>
      </svg>
    `;
  };

  const renderSpeedometer = (category: any, size: number = 140) => (
    <Grid item xs={6} sm={4} md={3} key={category.key}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          textAlign: 'center',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <Box dangerouslySetInnerHTML={{ __html: drawSpeedometer(category.value, size) }} />
        <Typography variant="body2" fontWeight="600" sx={{ mt: 1 }}>
          {category.name}
        </Typography>
      </Paper>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '🏎️ לוח מד מהירות - תצוגת טכומטר' : '🏎️ Speedometer Dashboard - Tachometer View'}
      </Typography>

      {/* Master Speedometers */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={4}
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              🏢 {language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
              <Box dangerouslySetInnerHTML={{ __html: drawSpeedometer(processedScores.businessAverage, 200) }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
              {language === 'he' ? 'ממוצע 7 קטגוריות עסקיות' : 'Average of 7 business categories'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={4}
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              👤 {language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
              <Box dangerouslySetInnerHTML={{ __html: drawSpeedometer(processedScores.personalAverage, 200) }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
              {language === 'he' ? 'ממוצע 3 קטגוריות אישיות' : 'Average of 3 personal categories'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Business Speedometers */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '2px solid #2c3e50' }}>
          🏢 {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.business.map(cat => renderSpeedometer(cat, 140))}
        </Grid>
      </Paper>

      {/* Personal Speedometers */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '2px solid #8e44ad' }}>
          👤 {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.personal.map(cat => renderSpeedometer(cat, 140))}
        </Grid>
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight="600" sx={{ mb: 1, textAlign: 'center' }}>
          {language === 'he' ? 'מקרא צבעים' : 'Color Legend'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#e74c3c', borderRadius: '50%' }} />
            <Typography variant="caption">0-40%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#f39c12', borderRadius: '50%' }} />
            <Typography variant="caption">40-60%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#2ecc71', borderRadius: '50%' }} />
            <Typography variant="caption">60-80%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#27ae60', borderRadius: '50%' }} />
            <Typography variant="caption">80-100%</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SpeedometerDashboard;
